import { useCallback, useMemo } from "react";
import { Stack } from "@mui/material";
import { debug as debugFn } from "debug";
import { errAsync, ResultAsync } from "neverthrow";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { IncidentWorkItemStatusSchema } from "@thalamos/common";
import type {
  IncidentFormId,
  IncidentWorkItemWithContext,
  RecordPlaceOfSafetyArrivalMinimumFields,
  RecordPlaceOfSafetyMinimumFields,
  WorkflowVersion,
} from "@thalamos/vision-common";
import {
  FormSectionSchema,
  getWorkflowTranslationContext,
  IncidentFormIdSchema,
  IncidentFormTypeSchema,
  IncidentWorkItemIdSchema,
  tk,
  workflows,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import { Banner } from "../../components/Banner/Banner.js";
import { useUser } from "../../Context/UserContext.js";
import type { ArrivePlaceOfSafetyFormProps } from "../../forms/ArrivePlaceOfSafety/ArrivePlaceOfSafetyForm.js";
import {
  ArrivePlaceOfSafetyForm,
  ArrivePlaceOfSafetyFormSkeleton,
} from "../../forms/ArrivePlaceOfSafety/ArrivePlaceOfSafetyForm.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { createRoute } from "../../routing/createRoute.js";
import { Error as ErrorComponent } from "../Error/Error.js";

const debug = debugFn("vision-frontend:ArrivePlaceOfSafetyPage");

export type ArrivePlaceOfSafetyPageProps = Record<string, unknown>;

// This component is responsible for managing the API calls and passing
// the data to the inner component.
export function ArrivePlaceOfSafetyPage(_props: ArrivePlaceOfSafetyPageProps) {
  const params = useParams();
  const { user } = useUser();

  const incidentId = IncidentWorkItemIdSchema.safeParse(params.id);
  if (!incidentId.success) {
    throw new Error("Invalid incident work item ID");
  }

  const [
    { data: incidentWorkItem, error: incidentWorkItemError },
    refetchWorkItem,
  ] = apiHooks.incidentWorkItem.get(incidentId.data);

  const placeOfSafetyFormIdResult = IncidentFormIdSchema.safeParse(
    params.formId,
  );

  if (
    incidentWorkItemError ||
    !placeOfSafetyFormIdResult.success ||
    user === undefined
  ) {
    return <ErrorComponent />;
  }

  const placeOfSafetyFormId = placeOfSafetyFormIdResult.data;

  return (
    <ArrivePlaceOfSafetyPageInner
      incidentWorkItem={incidentWorkItem ?? undefined}
      refetchWorkItem={refetchWorkItem}
      formId={placeOfSafetyFormId}
    />
  );
}

export type ArrivePlaceOfSafetyPageInnerProps = {
  incidentWorkItem?: IncidentWorkItemWithContext;
  refetchWorkItem: ReturnType<typeof apiHooks.incidentWorkItem.get>[1];
  formId: IncidentFormId;
};

export function ArrivePlaceOfSafetyPageInner({
  incidentWorkItem,
  refetchWorkItem,
  formId,
}: ArrivePlaceOfSafetyPageInnerProps) {
  return incidentWorkItem === undefined ? (
    <ArrivePlaceOfSafetyPageInnerLoading />
  ) : (
    <ArrivePlaceOfSafetyPageInnerLoaded
      incidentWorkItem={incidentWorkItem}
      refetchWorkItem={refetchWorkItem}
      formId={formId}
    />
  );
}

export function ArrivePlaceOfSafetyPageInnerLoading() {
  return <ArrivePlaceOfSafetyFormSkeleton />;
}

export function ArrivePlaceOfSafetyPageInnerLoaded({
  incidentWorkItem,
  refetchWorkItem,
  formId,
}: Required<ArrivePlaceOfSafetyPageInnerProps>) {
  const navigate = useNavigate();
  const toastNotifications = useToastNotifications();
  const { t } = useTranslation();
  const translationOptions = {
    context: getWorkflowTranslationContext({ incidentWorkItem }),
  };

  // Get incident information
  const incidentWorkItemId = incidentWorkItem.incidentWorkItem.id;
  const workflowVersion = incidentWorkItem.incidentWorkItem
    .workflowVersion as WorkflowVersion;

  // Get place of safety information
  const placeOfSafetyName = useMemo(
    () =>
      (
        incidentWorkItem.forms
          .find((form) => form.id === formId)
          ?.sections.find(
            (section) =>
              section.id === FormSectionSchema.enum.recordPlaceOfSafetyMain,
          )?.data as Partial<RecordPlaceOfSafetyMinimumFields> | null
      )?.placeOfSafety?.name,
    [incidentWorkItem, formId],
  );

  if (!placeOfSafetyName) {
    throw new Error("Place of safety name not collected");
  }

  // Get the schema for the "arrive at place-of-safety" form
  const schema = useMemo(
    () =>
      workflows[workflowVersion]?.recordPlaceOfSafety.formDefinition.sections
        .find(
          (section) =>
            section.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival,
        )
        ?.buildFormSpecification({ incidentWorkItem }).formSchema,
    [workflowVersion, incidentWorkItem],
  );

  if (!schema) {
    throw new Error("Schema not found");
  }

  // Calculate the initial values for the form
  const [initialValues, initialValuesWereValid] = useMemo(() => {
    // Get the existing values from the form if it exists
    const existingValues =
      incidentWorkItem.forms
        .find(
          (form) =>
            form.formType === IncidentFormTypeSchema.enum.recordPlaceOfSafety &&
            form.id === formId,
        )
        ?.sections.find(
          (section) =>
            section.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival,
        )?.data ?? {};

    const existingValuesWereValid = schema.safeParse(existingValues).success;

    // This is where we will acculmulate the values for the form
    const finalValues: Record<string, unknown> = { ...existingValues };

    return [finalValues, existingValuesWereValid];
  }, [formId, incidentWorkItem.forms, schema]);

  // Should we enable auto-save?
  const enableAutoSave = useMemo(
    () =>
      workflows[
        workflowVersion
      ]?.recordPlaceOfSafety.formDefinition.sections.find(
        (section) =>
          section.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival,
      )?.autoSave ?? false,
    [workflowVersion],
  );

  const onAutoSave: Exclude<
    ArrivePlaceOfSafetyFormProps<typeof workflowVersion>["onAutoSave"],
    undefined
  > = useCallback(
    (values) => {
      if (initialValuesWereValid) {
        // Because we started off with a valid and complete form, we should not regress
        // to a partial form.
        const isValid = schema.safeParse(values).success;
        if (!isValid) {
          return errAsync("Blocked auto-save: form is not valid");
        }
      }

      return ResultAsync.fromPromise(
        api.incidentWorkItem.updateData(
          incidentWorkItemId,
          formId,
          FormSectionSchema.enum.recordPlaceOfSafetyArrival,
          values,
          initialValuesWereValid,
          // TODO: ASP-3430 We don't update the status here, but rely on the submit.
          // Will this be a source of bugs?
        ),
        (error) =>
          error instanceof Error ? error.message : JSON.stringify(error),
      )
        .orTee(debug)
        .map(() => true);
    },
    [formId, incidentWorkItemId, initialValuesWereValid, schema],
  );

  const onSubmit: Exclude<
    ArrivePlaceOfSafetyFormProps<typeof workflowVersion>["onSubmit"],
    undefined
  > = useCallback(
    async (values: Record<string, unknown>) => {
      const didArriveAtPlaceOfSafety =
        (values as Partial<RecordPlaceOfSafetyArrivalMinimumFields> | null)
          ?.arrivalDateTime ?? undefined;

      try {
        // Populate it with values
        const populateFormResponse = await api.incidentWorkItem.updateData(
          incidentWorkItemId,
          formId,
          FormSectionSchema.enum.recordPlaceOfSafetyArrival,
          values,
          true,
          // TODO: ASP-3430 This is probably a lurking bug. Just because we have arrived,
          // it doesn't mean that this is the correct state, as there are states
          // beyond this.
          didArriveAtPlaceOfSafety !== undefined
            ? IncidentWorkItemStatusSchema.enum["arrived-at-place-of-safety"]
            : undefined,
        );
        if (populateFormResponse.status !== 204) {
          throw Error(JSON.stringify(populateFormResponse.data.message));
        }

        // Update the active officer request to null so it resets the police presence active form
        const updateActiveOfficerFormResponse =
          await api.incidentWorkItem.updateActiveOfficerRequest(
            incidentWorkItemId,
            null,
          );
        if (updateActiveOfficerFormResponse.status !== 204) {
          throw Error(
            JSON.stringify(updateActiveOfficerFormResponse.data.message),
          );
        }

        // Refetch the data as we have mutated the incident work item.
        await refetchWorkItem();

        navigate(
          createRoute.incidentRecordPlaceOfSafetyLeavePage(
            incidentWorkItemId,
            formId,
          ),
        );
        return;
      } catch (error) {
        debug("Failed to update Arrival section", error);
        toastNotifications.show(
          t("incidentWorkItem.notificationFailedToSave"),
          {
            severity: "error",
          },
        );
      }
    },
    [
      formId,
      incidentWorkItemId,
      navigate,
      refetchWorkItem,
      t,
      toastNotifications,
    ],
  );

  const onCancel: Exclude<
    ArrivePlaceOfSafetyFormProps<typeof workflowVersion>["onCancel"],
    undefined
  > = useCallback(() => {
    navigate(createRoute.incidentProgressPage(incidentWorkItemId));
  }, [incidentWorkItemId, navigate]);

  return (
    <Stack
      sx={{
        margin: "1rem",
      }}
    >
      <Banner
        bannerType={"info"}
        body={{
          translationKey: tk("arrivePlaceOfSafety.identityBanner"),
          translationOptions: {
            ...translationOptions,
            placeOfSafetyName,
          },
        }}
      />
      <ArrivePlaceOfSafetyForm
        initialValues={initialValues}
        incidentWorkItem={incidentWorkItem}
        onAutoSave={enableAutoSave ? onAutoSave : undefined}
        onSubmit={onSubmit}
        onCancel={onCancel}
        workflowVersion={workflowVersion}
      />
    </Stack>
  );
}
